import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useState } from "react";
import { hasValue, simpleHash } from "@utils";
import { UXPContext } from "@core/context/Context";
import { HeaderNavigation } from "./HeaderNavigation";
import classNames from "classnames";
import UserProfile from "./components/UserProfile";
import ThemeChanger from "@theme/ThemeChanger";
import NotificationCenter from "@core/notification-center/NotificationCenter";
import { SkeletonLoader } from "@widget/loaders";
import Icon from "@icon/Icon";
import { useIsPHIconReady } from "@icon/phosphor/useIsPHIconReady";
import { useMessageBus } from "@realtime/messagebus/useMessageBus";
import { PermissionChangedBanner } from "./PermissionChangedBanner";
import { ExportInProgressBanner } from "./ExportInProgressBanner";
import { BannerStack } from "./BannerStack";
import { SafeLink } from "@core/route-context/RouteContext";
const SESSION_EXPIRY_STORAGE_KEY = 'uxp-session-expiry';
const HeaderBase = (props) => {
    const context = useContext(UXPContext);
    const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
    function handlePermissionChanged(payload) {
        var _a;
        if (context.isPublicSession)
            return;
        try {
            const parsed = JSON.parse(payload);
            const myGroup = (_a = context.userDetails) === null || _a === void 0 ? void 0 : _a.UserGroupKey;
            const affected = (parsed.userKey && parsed.userKey === context.userKey) ||
                (parsed.userGroupKey && parsed.userGroupKey === myGroup) ||
                (Array.isArray(parsed.userGroupKeys) && myGroup && parsed.userGroupKeys.includes(myGroup));
            if (affected && typeof parsed.expiresAt === 'number') {
                setSessionExpiresAt(parsed.expiresAt);
                // Survive page refreshes: the SignalR message is one-shot, but the session
                // still dies at expiresAt. Keyed to a hash of the session token so a
                // re-login (new token) never resurrects a stale banner.
                try {
                    localStorage.setItem(SESSION_EXPIRY_STORAGE_KEY, JSON.stringify({
                        expiresAt: parsed.expiresAt,
                        token: simpleHash(context.apiKey)
                    }));
                }
                catch (_b) { }
            }
        }
        catch (_c) { }
    }
    useMessageBus(context, 'iviva_usergroup_permissions_changed', handlePermissionChanged);
    // Restore a pending expiry countdown after a refresh (or in a newly opened tab).
    useEffect(() => {
        if (context.isPublicSession || !context.apiKey)
            return;
        try {
            const raw = localStorage.getItem(SESSION_EXPIRY_STORAGE_KEY);
            if (!raw)
                return;
            const stored = JSON.parse(raw);
            if ((stored === null || stored === void 0 ? void 0 : stored.token) === simpleHash(context.apiKey) && stored.expiresAt > Math.floor(Date.now() / 1000)) {
                setSessionExpiresAt(stored.expiresAt);
            }
            else {
                localStorage.removeItem(SESSION_EXPIRY_STORAGE_KEY);
            }
        }
        catch (_a) { }
    }, []);
    const renderTitle = useCallback(() => {
        if (!hasValue(context.title))
            return null;
        return _jsx(HeaderItemWrapper, { children: _jsx("div", { className: "uxpcore_dashboardtitle__container", children: context.title }) });
    }, [context]);
    const logo = _jsx("div", { className: "uxpcore_header__logo", style: {
            backgroundImage: `url(${props.logo})`
        } });
    const banners = _jsxs(BannerStack, { children: [sessionExpiresAt !== null && _jsx(PermissionChangedBanner, { expiresAt: sessionExpiresAt }), _jsx(ExportInProgressBanner, {})] });
    if (props.isMobile)
        return _jsxs(_Fragment, { children: [banners, _jsxs("div", { className: "uxpcore_header__container uxpcore_header__container--mobile", children: [_jsxs("div", { className: "uxpcore_header__left", children: [context.isPublicSession && (_jsx("button", { className: "uxpcore_header__menubtn", onClick: props.onOpenMenu, "aria-label": context.$L('uxp-core.text.menu'), children: _jsx(Icon, { icon: ['phl', 'list'] }) })), _jsxs("div", { className: "uxpcore_header__brand", children: [context.isPublicSession
                                            ? logo
                                            : _jsx(SafeLink, { to: "/view/system/home", className: "uxpcore_header__logo_link", children: logo }), hasValue(props.pageTitle) && _jsx("div", { className: "uxpcore_header__pagetitle", children: props.pageTitle })] })] }), context.isPublicSession && (_jsx("div", { className: "uxpcore_header__right", children: _jsx(HeaderItemWrapper, { children: _jsx(ThemeChanger, {}) }) }))] })] });
    return _jsxs(_Fragment, { children: [banners, _jsxs("div", { className: "uxpcore_header__container", children: [_jsxs("div", { className: "uxpcore_header__left", children: [_jsx(SidebarToggleButton, { expandedSidebar: props.expandedSidebar, onClick: props.onToggleSidebar }), _jsx("div", { className: "uxpcore_header__logo_container", children: context.isPublicSession
                                    ? logo
                                    : _jsx(SafeLink, { to: "/view/system/home", className: "uxpcore_header__logo_link", children: logo }) })] }), _jsxs("div", { className: "uxpcore_header__middle", children: [renderTitle(), _jsx(HeaderNavigation, { navigationLinks: props === null || props === void 0 ? void 0 : props.navigationLinks })] }), _jsxs("div", { className: "uxpcore_header__right", children: [props === null || props === void 0 ? void 0 : props.renderRightSection, !context.isPublicSession && (_jsx(HeaderItemWrapper, { children: _jsx(NotificationCenter, {}) })), context.isPublicSession && (_jsx(HeaderItemWrapper, { children: _jsx(ThemeChanger, {}) })), !context.isPublicSession && (_jsx(HeaderItemWrapper, { children: _jsx(UserProfile, {}) }))] })] })] });
};
export const Header = memo(HeaderBase);
export const HeaderItemWrapper = (props) => {
    return _jsx("div", { className: "uxpcore_headeritem__container", children: props.children });
};
const SidebarToggleButton = (props) => {
    const context = useContext(UXPContext);
    const isRTL = (context === null || context === void 0 ? void 0 : context.direction) === 'rtl';
    // LTR: expanded = text-outdent, collapsed = text-indent
    // RTL: flip the logic (expanded = text-indent, collapsed = text-outdent)
    const iconName = isRTL
        ? (props.expandedSidebar ? 'text-indent' : 'text-outdent')
        : (props.expandedSidebar ? 'text-outdent' : 'text-indent');
    const ready = useIsPHIconReady(iconName);
    return _jsx("div", { className: classNames("uxpcore_sidebartogglebtn__container", {
            "uxpcore_sidebartogglebtn__container--expandedsidebar": props.expandedSidebar
        }), onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
            props.onClick();
        }, children: _jsx("div", { className: "uxpcore_sidebartogglebtn__menuicon", children: ready
                ? _jsx(Icon, { icon: ['phl', iconName] })
                : _jsx(SkeletonLoader, { width: "100%", height: "100%", additionalStyle: {
                        borderRadius: '50%',
                        aspectRatio: '1'
                    } }) }) });
};
